export type CardBrand = 'visa' | 'mastercard' | 'amex' | 'discover' | 'unknown';

export interface CardDetection {
  brand: CardBrand;
  cyberSourceCardTypeCode: '001' | '002' | '003' | '004';
  expectedPanLength: number;
  cvvLength: 3 | 4;
}

const DEFAULT: CardDetection = {
  brand: 'unknown',
  cyberSourceCardTypeCode: '001',
  expectedPanLength: 16,
  cvvLength: 3,
};

function digitsPrefix(pan: string, len: number): string {
  const d = pan.replace(/\D/g, '');
  return d.slice(0, len);
}

function inMastercardRange(firstFour: number): boolean {
  return firstFour >= 2221 && firstFour <= 2720;
}

/**
 * BIN heuristic for display, CyberSource card type code, and CVV length.
 * PAN length is a hint for UX; Luhn + 13–19 validation still applies on the form.
 */
export function detectCardFromPanInput(pan: string): CardDetection {
  const d = pan.replace(/\D/g, '');
  if (d.length === 0) {
    return { ...DEFAULT };
  }

  const c1 = d[0];
  const c2 = d.length >= 2 ? d.slice(0, 2) : '';
  const c4 = d.length >= 4 ? d.slice(0, 4) : '';
  const c4n = c4.length === 4 ? parseInt(c4, 10) : NaN;

  if (c1 === '4') {
    return {
      brand: 'visa',
      cyberSourceCardTypeCode: '001',
      expectedPanLength: 16,
      cvvLength: 3,
    };
  }

  if (c2 === '34' || c2 === '37') {
    return {
      brand: 'amex',
      cyberSourceCardTypeCode: '003',
      expectedPanLength: 15,
      cvvLength: 4,
    };
  }

  if (c4 === '6011' || c2 === '65') {
    return {
      brand: 'discover',
      cyberSourceCardTypeCode: '004',
      expectedPanLength: 16,
      cvvLength: 3,
    };
  }

  if (c2.length === 2) {
    const two = parseInt(c2, 10);
    if (two >= 51 && two <= 55) {
      return {
        brand: 'mastercard',
        cyberSourceCardTypeCode: '002',
        expectedPanLength: 16,
        cvvLength: 3,
      };
    }
  }

  if (!Number.isNaN(c4n) && inMastercardRange(c4n)) {
    return {
      brand: 'mastercard',
      cyberSourceCardTypeCode: '002',
      expectedPanLength: 16,
      cvvLength: 3,
    };
  }

  return { ...DEFAULT };
}

export function stripCardNumberSpaces(value: string): string {
  return value.replace(/\s/g, '');
}
